/**
 * Customers list — every non-internal org with computed metrics.
 *
 * Phase-1 surface: a single sortable / filterable table. Clicking
 * a row navigates to the per-customer detail page. The data feed
 * (``/admin/customers``) returns the table verbatim — no
 * client-side joins needed.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search } from "lucide-react";

import { CustomerRow, listCustomers } from "@/api/admin";
import { cn, fmtNum, fmtRelative, fmtUsd } from "@/lib/utils";

export function CustomersPage() {
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    listCustomers()
      .then((d) => !cancelled && setRows(d))
      .catch((e) =>
        !cancelled && setError(e?.message || "Failed to load customers"),
      );
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const f = filter.trim().toLowerCase();
    if (!f) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(f) ||
        (r.plan || "").toLowerCase().includes(f) ||
        (r.status || "").toLowerCase().includes(f),
    );
  }, [rows, filter]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {rows ? `${rows.length} total` : "—"} non-internal orgs.
          </p>
        </div>
        <label className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filter by name / plan / status"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-80 pl-8 pr-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent bg-white"
          />
        </label>
      </header>

      {error && (
        <div className="rounded-md bg-danger/5 border border-danger/30 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {!rows && (
          <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {rows && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Org</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Plan</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Employees</th>
                  <th className="text-right px-4 py-2.5 font-semibold">MRR</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Credits</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Last active</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Created</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No customers match.
                    </td>
                  </tr>
                )}
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/customers/${c.id}`}
                        className="font-medium text-slate-900 hover:text-accent"
                      >
                        {c.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {planLabel(c.plan)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {fmtNum(c.employees)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {fmtUsd(c.monthly_revenue_usd)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">
                      <span title={`Allowance: ${c.credits_allowance}; Purchased: ${c.purchased_credits}`}>
                        {fmtNum(c.credits_used)} / {fmtNum(c.credits_allowance)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {fmtRelative(c.last_active_at)}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {fmtRelative(c.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function planLabel(plan: string): string {
  const labels: Record<string, string> = {
    starter: "Starter",
    growth: "Professional",
    enterprise: "Enterprise",
    free: "Free",
  };
  return labels[(plan || "").toLowerCase()] || plan || "—";
}

function StatusBadge({ status }: { status: string }) {
  const tone: Record<string, string> = {
    active: "bg-success/10 text-success border-success/30",
    trial: "bg-accent/10 text-accent border-accent/30",
    expired: "bg-warning/10 text-warning border-warning/30",
    cancelled: "bg-slate-100 text-slate-500 border-slate-200",
    past_due: "bg-danger/10 text-danger border-danger/30",
    free: "bg-slate-50 text-slate-400 border-slate-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize",
        tone[status] || tone.free,
      )}
    >
      {status === "past_due" ? "past due" : status}
    </span>
  );
}
